/**
 * /onboarding — post-sign-up bridge.
 *
 * Creates the first organisation for a signed-in user who doesn't
 * have one yet, then hands off to `/`. Users who already belong to
 * an organisation get auto-redirected home so the URL is safe to
 * land on from anywhere.
 *
 * Flow:
 *   useUser() → signed in, no active org
 *   useOrganizationList().createOrganization({ name })
 *   setActive({ organization: newOrg.id })  → navigate('/')
 *
 * This is the minimum viable onboarding surface. Stage 5 replaces
 * with a richer stepper (team invite, first key, first origin).
 */

import { Logo } from '@/components/shell/Logo';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { mapClerkError } from '@/lib/mapClerkError';
import { useAuth, useOrganizationList, useUser } from '@clerk/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useEffect, useState } from 'react';

export const Route = createFileRoute('/onboarding')({
  component: OnboardingPage,
});

function OnboardingPage(): ReactElement {
  const { isLoaded: authLoaded, isSignedIn, orgId } = useAuth();
  const { user } = useUser();
  const orgList = useOrganizationList({ userMemberships: { infinite: false } });
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);

  const firstName = user?.firstName ?? undefined;

  // Route away if we shouldn't be here.
  useEffect(() => {
    if (!authLoaded) return;
    if (!isSignedIn) {
      navigate({ to: '/sign-in', replace: true });
      return;
    }
    if (orgId) {
      navigate({ to: '/', replace: true });
    }
  }, [authLoaded, isSignedIn, orgId, navigate]);

  // Suggest a sensible workspace name based on the user.
  useEffect(() => {
    if (name) return;
    if (firstName) {
      setName(`${firstName}'s workspace`);
    }
  }, [firstName, name]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!orgList.isLoaded || !orgList.createOrganization || !orgList.setActive) return;
    if (!name.trim()) {
      setFieldErrors({ name: 'Give your workspace a name.' });
      return;
    }

    setSubmitting(true);
    setFieldErrors({});
    setFormError(undefined);

    try {
      const org = await orgList.createOrganization({ name: name.trim() });
      await orgList.setActive({ organization: org.id });
      navigate({ to: '/', replace: true });
    } catch (err) {
      const mapped = mapClerkError(err);
      setFieldErrors(mapped.fieldErrors);
      setFormError(mapped.formError);
      setSubmitting(false);
    }
  }

  if (!authLoaded || !isSignedIn || orgId) {
    return (
      <div className="flex min-h-dvh w-full items-center justify-center">
        <Spinner className="size-5 text-[var(--color-mute)]" />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh w-full flex-col items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-5 flex items-center justify-center gap-2 text-[var(--color-accent-brand)]">
          <Logo size={28} />
          <span className="text-base font-semibold text-[var(--color-ink)]">ClickWeave</span>
        </div>
        <div
          className={
            'rounded-[var(--radius-panel)] border border-[var(--color-line)] ' +
            'bg-[var(--color-paper)] p-8 shadow-[var(--shadow-panel)]'
          }
        >
          <header className="space-y-1 text-center">
            <h1 className="text-xl font-semibold text-[var(--color-ink)]">
              {firstName ? `Welcome, ${firstName}.` : 'Welcome to ClickWeave.'}
            </h1>
            <p className="text-sm text-[var(--color-mute)]">
              Name your workspace. You can invite teammates once you're in.
            </p>
          </header>
          <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
            <Field>
              <FieldLabel htmlFor="org-name">Workspace name</FieldLabel>
              <Input
                id="org-name"
                autoFocus
                value={name}
                onChange={(event) => setName((event.target as HTMLInputElement).value)}
                aria-invalid={fieldErrors.name ? true : undefined}
                required
              />
              {fieldErrors.name && <FieldError>{fieldErrors.name}</FieldError>}
            </Field>

            {formError && (
              <div
                className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
                role="alert"
              >
                {formError}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Creating workspace…' : 'Continue'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
